import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { IVehicleAddress } from '../../../../../interfaces/task-info';
import { IRecommendationAdjustedTask } from '../../../../../interfaces/route-recommendations/recommendation-task';
import { ISupplier } from '../../../../../interfaces/supplier';
import { VmePipesModule } from '../../../../../vme-pipes.module';
import { VmeDirectivesModule } from '../../../../../vme-directives.module';
import { CommonModule } from '@angular/common';
import { What3wordsComponent } from '../../../../logos/what3words/what3words.component';

export interface ItineraryFromToLocation {
  address: IVehicleAddress;
  earliestTime: Date | string;
  latestTime: Date | string;
  timeOnSite?: string;
}

@Component({
  selector: 'route-itinerary-task-address-detail',
  templateUrl: './route-itinerary-task-address-detail.component.html',
  styleUrls: [
    './route-itinerary-task-address-detail.component.scss',
    '../../common/scss/route-itinerary-common.scss',
  ],
  standalone: true,
  imports: [VmePipesModule, VmeDirectivesModule, CommonModule, What3wordsComponent],
})
export class RouteItineraryTaskAddressDetailComponent implements OnChanges {
  @Input() task: IRecommendationAdjustedTask;
  @Input() carryOverFromHome: boolean = false;
  @Input() carryOverToHome: boolean = false;
  @Input() supplier: ISupplier;

  pickupAddress: ItineraryFromToLocation;
  dropOffAddress: ItineraryFromToLocation;

  movementTime: number;
  distance: number;
  chargeTime: number;

  supplierAddress(): IVehicleAddress {
    return {
      businessName: this.supplier?.name,
      addressLine1: this.supplier?.addressLine1,
      addressLine2: this.supplier?.addressLine2,
      addressLine3: this.supplier?.addressLine3,
      postcode: this.supplier?.postcode,
      latitude: this.supplier?.latitude,
      longitude: this.supplier?.longitude,
    } as IVehicleAddress;
  }

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['task'] ||
      changes['carryOverFromHome'] ||
      changes['carryOverToHome'] ||
      changes['supplier']
    ) {
      this.setAddresses();
    }
  }

  setAddresses(): void {
    if (this.carryOverFromHome) {
      this.pickupAddress = this.carryOverSecondLegFrom();
      this.dropOffAddress = this.carryOverSecondLegTo();

      this.movementTime = this.task.taskCarryover.estimatedHomeToDropoffTime;
      this.distance = this.task.taskCarryover.homeToDropoffDistance;
      this.chargeTime = this.task.taskCarryover.homeToDropoffEstimatedChargeTime;
    } else if (this.carryOverToHome) {
      this.pickupAddress = this.carryOverFirstLegFrom();
      this.dropOffAddress = this.carryOverFirstLegTo();

      this.movementTime = this.task.taskCarryover.estimatedPickupToHomeTime;
      this.distance = this.task.taskCarryover.pickupToHomeDistance;
      this.chargeTime = this.task.taskCarryover.pickupToHomeEstimatedChargeTime;
    } else {
      this.pickupAddress = this.standardLegFrom();
      this.dropOffAddress = this.standardLegTo();

      this.movementTime = this.task.estimatedMovementTime;
      this.distance = this.task.task.distance;
      this.chargeTime = this.task.estimatedChargeTime;
    }
  }

  carryOverFirstLegFrom(): ItineraryFromToLocation {
    if (!this.task.hasCarryover) {
      return null;
    }
    return {
      address: this.task.task.pickupLocation,
      earliestTime: this.task.pickupEarliestTime,
      latestTime: this.task.pickupLatestTime,
      timeOnSite: this.task.pickupHandoverRangeString,
    };
  }

  carryOverFirstLegTo(): ItineraryFromToLocation {
    if (!this.task.hasCarryover) {
      return null;
    }
    return {
      address: this.supplierAddress(),
      earliestTime: this.task.taskCarryover.arriveHomeEarliest,
      latestTime: this.task.taskCarryover.arriveHomeLatest,
    };
  }

  carryOverSecondLegFrom(): ItineraryFromToLocation {
    if (!this.task.hasCarryover) {
      return null;
    }
    return {
      address: this.supplierAddress(),
      earliestTime: this.task.taskCarryover.leaveHomeEarliest,
      latestTime: this.task.taskCarryover.leaveHomeLatest,
    };
  }

  carryOverSecondLegTo(): ItineraryFromToLocation {
    if (!this.task.hasCarryover) {
      return null;
    }
    return {
      address: this.task.task.dropoffLocation,
      earliestTime: this.task.dropoffEarliestTime,
      latestTime: this.task.dropoffLatestTime,
      timeOnSite: this.task.dropoffHandoverRangeString,
    };
  }

  standardLegFrom(): ItineraryFromToLocation {
    return {
      address: this.task.task.pickupLocation,
      earliestTime: this.task.pickupEarliestTime,
      latestTime: this.task.pickupLatestTime,
      timeOnSite: this.task.pickupHandoverRangeString,
    };
  }

  standardLegTo(): ItineraryFromToLocation {
    return {
      address: this.task.task.dropoffLocation,
      earliestTime: this.task.dropoffEarliestTime,
      latestTime: this.task.dropoffLatestTime,
      timeOnSite: this.task.dropoffHandoverRangeString,
    };
  }
}
